import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, MenuItem, TextField } from '@mui/material';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import ConfirmationModal from '../../ConfirmationModal';
import { TechnicalIssue } from 'store/types/Meeting';
import { ActivityType, ConfirmationType, ItemType } from 'store/enums/BallotReviewTypes';
import { defaultSnackbarErrorProps } from 'util/layout';
import { useSafeSelectIdValue } from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

const idLabel = 'technicalIssue';

const getTechnicalIssueOptions = (technicalIssues: TechnicalIssue[]) =>
  technicalIssues.map((t) => (
    <MenuItem key={t.id} value={t.id}>
      {`Origin: ${t.origin} - Referenced: ${t.reference}`}
    </MenuItem>
  ));

const TechnicalIssueSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const {
    logActivity,
    technicalIssues,
    currentDocument,
    setCurrentTechnicalIssue,
    saveTechnicalIssue,
    deleteTechnicalIssue,
  } = useMeetingStaffContext();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTechnicalIssueId, setSelectedTechnicalIssueId] = useState('');
  const [draftTechnicalIssueId, setDraftTechnicalIssueId] = useState(() => `${idLabel}-${Date.now()}`);
  const [origin, setOrigin] = useState('');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');

  const technicalIssueOptions = useMemo(() => getTechnicalIssueOptions(technicalIssues), [technicalIssues]);

  const safeSelectedTechnicalIssueId = useSafeSelectIdValue(selectedTechnicalIssueId, technicalIssues);

  const currentTechnicalIssue: TechnicalIssue = useMemo(() => {
    return {
      id: selectedTechnicalIssueId || draftTechnicalIssueId,
      documentId: currentDocument?.id || '',
      origin,
      reference,
      reason,
    };
  }, [currentDocument?.id, draftTechnicalIssueId, origin, reason, reference, selectedTechnicalIssueId]);

  const handleTechnicalIssueChange = useCallback((event) => {
    const technicalIssueId = event.target.value;

    setSelectedTechnicalIssueId(technicalIssueId);
    if (!technicalIssueId) {
      setDraftTechnicalIssueId(`${idLabel}-${Date.now()}`);
    }
  }, []);

  const handleSaveTechnicalIssue = useCallback(() => {
    saveTechnicalIssue?.().then((saved) => {
      if (saved) {
        enqueueSnackbar(`Technical issue saved`, { variant: 'success' });
        setSelectedTechnicalIssueId(saved.id);
        setDraftTechnicalIssueId(`${idLabel}-${Date.now()}`);
      }
    });
  }, [enqueueSnackbar, saveTechnicalIssue]);

  const handleDeleteTechnicalIssue = useCallback(() => {
    if (!selectedTechnicalIssueId) {
      enqueueSnackbar(`No technical issue selected to delete`, { variant: 'warning' });
      return;
    }

    deleteTechnicalIssue?.(selectedTechnicalIssueId);
  }, [selectedTechnicalIssueId, deleteTechnicalIssue, enqueueSnackbar]);

  const handleDisplayTechnicalIssue = useCallback(() => {
    if (!meetingSession?.currentDocument?.id) {
      enqueueSnackbar(`Document must be displayed first`, { variant: 'warning' });
      return;
    }

    saveTechnicalIssue?.().then((saved) => {
      if (!saved) return;

      setSelectedTechnicalIssueId(saved.id);
      setDraftTechnicalIssueId(`${idLabel}-${Date.now()}`);

      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentReject: undefined,
        currentTechnicalIssue: saved,
        currentComment: undefined,
      })
        .then(() => {
          logActivity?.({
            type: ActivityType.TechnicalIssue,
            document: meetingSession.currentDocument,
            technicalIssue: saved,
          });

          enqueueSnackbar(`Displayed technical issue`, { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(`Failed to display technical issue`, defaultSnackbarErrorProps);
          console.error('Error displaying technical issue:', error);
        });
    });
  }, [enqueueSnackbar, logActivity, meetingId, meetingSession, saveTechnicalIssue]);

  const handleClearTechnicalIssue = useCallback(() => {
    setSelectedTechnicalIssueId('');
    setDraftTechnicalIssueId(`${idLabel}-${Date.now()}`);
    setOrigin('');
    setReference('');
    setReason('');

    MeetingService.saveMeetingSession({
      ...meetingSession,
      meetingId: meetingId,
      currentReject: undefined,
      currentTechnicalIssue: undefined,
      currentComment: undefined,
    })
      .then(() => {
        enqueueSnackbar(`Cleared technical issue`, { variant: 'success' });
      })
      .catch((error: string) => {
        enqueueSnackbar(`Failed to display technical issue`, defaultSnackbarErrorProps);
        console.error('Error displaying technical issue:', error);
      });
  }, [enqueueSnackbar, meetingId, meetingSession]);

  const handleSaveClick = useCallback(() => {
    if (selectedTechnicalIssueId) {
      setSaveModalOpen(true);
      return;
    }
    handleSaveTechnicalIssue();
  }, [handleSaveTechnicalIssue, selectedTechnicalIssueId]);

  const handleSaveModalSubmit = useCallback(() => {
    setSaveModalOpen(false);
    handleSaveTechnicalIssue();
  }, [handleSaveTechnicalIssue]);

  const handleDeleteClick = useCallback(() => {
    if (selectedTechnicalIssueId) {
      setDeleteModalOpen(true);
      return;
    }
    handleDeleteTechnicalIssue();
  }, [handleDeleteTechnicalIssue, selectedTechnicalIssueId]);

  const handleDeleteModalSubmit = useCallback(() => {
    setDeleteModalOpen(false);
    handleDeleteTechnicalIssue();
  }, [handleDeleteTechnicalIssue]);

  useEffect(() => {
    const technicalIssue = technicalIssues.find((d) => d.id === selectedTechnicalIssueId);

    if (technicalIssue) {
      setOrigin(technicalIssue?.origin || '');
      setReference(technicalIssue?.reference || '');
      setReason(technicalIssue?.reason || '');
    }
  }, [selectedTechnicalIssueId, technicalIssues, technicalIssues?.length]);

  useEffect(() => {
    setCurrentTechnicalIssue?.(currentTechnicalIssue);
  }, [currentTechnicalIssue, setCurrentTechnicalIssue]);

  return (
    <>
      {' '}
      <GridContainer>
        <GridItem md={6}>
          <TextField
            label={'Load Technical Issue'}
            select
            value={safeSelectedTechnicalIssueId}
            onChange={handleTechnicalIssueChange}
          >
            <MenuItem value={''}>-- New Technical Issue --</MenuItem>
            {technicalIssueOptions}
          </TextField>
        </GridItem>
        <GridItem md={6} className={commonStyles.buttonGroup}>
          <Button variant="contained" color="primary" onClick={handleSaveClick}>
            Save
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteClick}>
            Delete
          </Button>
        </GridItem>
        <GridItem>
          <TextField
            label={'Origin'}
            value={origin}
            required
            multiline
            onChange={(event) => {
              setOrigin(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem>
          <TextField
            label={'Referenced Section'}
            value={reference}
            required
            multiline
            onChange={(event) => {
              setReference(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem>
          <TextField
            label={'Reason'}
            value={reason}
            required
            multiline
            onChange={(event) => {
              setReason(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem className={commonStyles.buttonGroup}>
          <Button variant="contained" color="secondary" onClick={handleDisplayTechnicalIssue}>
            Display Technical Issue
          </Button>
          <Button variant="contained" color="error" onClick={handleClearTechnicalIssue}>
            Clear Technical Issue
          </Button>
        </GridItem>
      </GridContainer>
      <ConfirmationModal
        itemType={ItemType.TechnicalIssue}
        confirmationType={ConfirmationType.Overwrite}
        open={saveModalOpen}
        onSubmit={handleSaveModalSubmit}
        onClose={() => setSaveModalOpen(false)}
      />
      <ConfirmationModal
        itemType={ItemType.TechnicalIssue}
        confirmationType={ConfirmationType.Delete}
        open={deleteModalOpen}
        onSubmit={handleDeleteModalSubmit}
        onClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export default TechnicalIssueSection;
